import React, { useState } from 'react';
import classNames from 'classnames';
import { Stack } from '@moda/om';
import { AuthPayloadSuccess } from '../../lib/authenticate';
import { DefaultValues } from './Authentication';
import { SignInFormValues } from './PasswordSignIn';
import { PasswordlessOtpStep } from './PasswordlessOtpStep';
import { PasswordlessRegistrationStep } from './PasswordlessRegistrationStep';
import { PasswordlessRequestStep } from './PasswordlessRequestStep';

type PasswordlessAuthenticationProps = {
  fullPage?: boolean;
  modal?: boolean;
  defaultValues?: DefaultValues;
  onAuth: (authUser?: AuthPayloadSuccess) => void;
  otpFooter?: (args: { email: string }) => React.ReactNode;
  registrationDefaultEmailList?: boolean;
  requestFieldsFooter?: React.ReactNode;
  requestFooter?: React.ReactNode;
  requestMessage?: string;
  requestTitle?: string;
  showCancelButton?: boolean;
  showSocialAuth?: boolean;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
};

type PasswordlessStep =
  | { step: 'request' }
  | { step: 'otp'; email: string; challengeId: string }
  | { step: 'registration'; email: string; registrationToken: string };

const isSignInFormValues = (values?: DefaultValues): values is Partial<SignInFormValues> =>
  values != null && 'signInEmail' in values;

export const PasswordlessAuthentication: React.FC<PasswordlessAuthenticationProps> = ({
  fullPage = false,
  modal = false,
  defaultValues,
  onAuth,
  otpFooter,
  registrationDefaultEmailList,
  requestFieldsFooter,
  requestFooter,
  requestMessage,
  requestTitle,
  showCancelButton,
  showSocialAuth = true,
  showAuthModal,
  hideAuthModal
}) => {
  const [step, setStep] = useState<PasswordlessStep>({ step: 'request' });

  const signInDefaultValues = isSignInFormValues(defaultValues) ? defaultValues : undefined;

  return (
    <div className="Authentication__content">
      <div
        className={classNames('Authentication__section', 'Authentication__section--active', {
          'Authentication__section--passwordlessFullPage': fullPage
        })}
        data-testid="passwordlessSignInSection"
      >
        {step.step === 'request' && (
          <Stack space={5}>
            <PasswordlessRequestStep
              defaultEmail={signInDefaultValues?.signInEmail}
              fieldsFooter={requestFieldsFooter}
              hideAuthModal={hideAuthModal}
              message={requestMessage}
              onAuth={onAuth}
              onRequested={({ challengeId, email }) => setStep({ step: 'otp', challengeId, email })}
              showAmazonMessage={modal}
              showAuthModal={showAuthModal}
              showCancelButton={showCancelButton ?? !fullPage}
              showPrivacyPolicy={modal}
              showSocialAuth={showSocialAuth}
              title={requestTitle}
            />
            {requestFooter}
          </Stack>
        )}
        {step.step === 'registration' && (
          <PasswordlessRegistrationStep
            defaultEmailListChecked={registrationDefaultEmailList}
            email={step.email}
            onAuth={onAuth}
            onCancel={() => setStep({ step: 'request' })}
            registrationToken={step.registrationToken}
            remember={signInDefaultValues?.signInRemember ?? true}
          />
        )}
        {step.step === 'otp' && (
          <PasswordlessOtpStep
            challengeId={step.challengeId}
            email={step.email}
            footer={otpFooter?.({ email: step.email })}
            onAuth={onAuth}
            onCancel={() => setStep({ step: 'request' })}
            onRegistrationRequired={registration =>
              setStep({ step: 'registration', ...registration })
            }
            remember={signInDefaultValues?.signInRemember ?? true}
          />
        )}
      </div>
    </div>
  );
};
